/**
 * TECH-2730. The save-time decisions for a message template, as pure data.
 *
 * WHY THIS EXISTS AS ITS OWN MODULE. These rules were written inside a
 * useCallback in EditEmailTemplateDialog, so nothing could test them, and this
 * screen had no tests at all. They are also the rules the accordion rebuild
 * has to preserve exactly — an inline card is a different component with the
 * same contract, and re-deriving the contract by reading the old component is
 * how it gets changed by accident.
 *
 * Every rule here exists because breaking it harms a patient rather than an
 * administrator:
 *
 *  - An INCOMPLETE base is rejected because an override only wins at send time
 *    when subject AND htmlContent are both non-empty; saving half of one gives
 *    an organisation a message it believes is customised and a patient the
 *    built-in copy.
 *
 *  - An EMPTY translation is dropped rather than saved, because the PEP
 *    resolver folds every offered language into its candidate map.
 *    mergeLanguagePreference would then select that language for a patient who
 *    prefers it, the all-or-nothing check would reject it, and the patient
 *    would be DOWNGRADED from the organisation's override to the built-in copy
 *    — worse than never having offered the language. Merely clicking a
 *    language tab creates one of these, so dropping is silent by design.
 *
 *  - A PARTLY FILLED translation is refused, not dropped. Dropping discarded
 *    real work while reporting success, so the loss repeated on every
 *    subsequent save of that row.
 *
 *  - A MISSING SMS BODY only warns. Raised and rejected twice in review: a
 *    complete translation has no problem, and this only concerns one saved
 *    with Content left blank, which yields a translated email and an English
 *    SMS. Warning the author is the agreed answer, which is precisely why the
 *    warning has to be readable (finding F1).
 */

export const EMPTY_EMAIL = {
  subject: '',
  content: '',
  htmlContent: '',
};

/**
 * Only sendSignUpRequest posts `content`, and only as the SMS body. Five of
 * the seven PEP purposes and both staff purposes never send it: for staff,
 * resolveAccountMessage derives text from htmlContent and never reads
 * `content` at all.
 *
 * Offering an SMS field for those is the same silent-discard defect this
 * codebase already guards against elsewhere — copy that is stored, counted,
 * and never delivered.
 */
export const contentIsSentForPurpose = (purpose) =>
  purpose === 'pep_signup' || purpose === 'pep_login';

const filled = (value) => !!(value || '').trim();

const isComplete = (email) =>
  filled(email.subject) && filled(email.htmlContent);

const hasAnything = (email) =>
  filled(email.subject) || filled(email.content) || filled(email.htmlContent);

/**
 * @param {object} args
 * @param {string} args.language        the record's base language
 * @param {object} args.emailByLanguage language -> {subject, content, htmlContent}
 * @param {string} args.purpose
 * @returns {{
 *   error: null | {key: string, params: object},
 *   baseEmail: object,
 *   translations: Array<{language: string, email: object}>,
 *   languagesMissingContent: string[],
 * }}
 *   `error` non-null means DO NOT SAVE. `languagesMissingContent` is a warning
 *   only and is populated only for the purposes that actually send an SMS.
 */
/**
 * TECH-2730. The i18n keys buildTemplateSubmission can hand back in `error.key`.
 *
 * Named and exported because the editor translates them with
 * t(error.key, error.params) — a key EXPRESSION, which the i18n coverage
 * guard cannot resolve by reading the source. The guard checks this list
 * against every locale instead, the way it takes the section headings from
 * SECTIONS. A new error key goes here, or the guard fails; a literal used
 * below but absent from this list is exactly the unchecked key the guard
 * exists to catch, so the returns below use these constants, not literals.
 */
export const SUBMISSION_ERROR_KEY__BASE_INCOMPLETE =
  'emailTemplateRequiresSubjectAndHtml';
export const SUBMISSION_ERROR_KEY__TRANSLATION_INCOMPLETE =
  'translationRequiresSubjectAndHtml';
export const SUBMISSION_ERROR_KEYS = [
  SUBMISSION_ERROR_KEY__BASE_INCOMPLETE,
  SUBMISSION_ERROR_KEY__TRANSLATION_INCOMPLETE,
];

export const buildTemplateSubmission = ({
  language,
  emailByLanguage,
  purpose,
}) => {
  const baseEmail = { ...EMPTY_EMAIL, ...(emailByLanguage || {})[language] };

  if (!isComplete(baseEmail)) {
    return {
      error: { key: SUBMISSION_ERROR_KEY__BASE_INCOMPLETE, params: {} },
      baseEmail,
      translations: [],
      languagesMissingContent: [],
    };
  }

  const candidates = Object.keys(emailByLanguage || {})
    .filter((lng) => lng !== language)
    .map((lng) => ({
      language: lng,
      email: { ...EMPTY_EMAIL, ...emailByLanguage[lng] },
    }));

  const partiallyFilled = candidates
    .filter(({ email }) => !isComplete(email) && hasAnything(email))
    .map(({ language: lng }) => lng.toUpperCase());

  if (partiallyFilled.length > 0) {
    return {
      error: {
        key: SUBMISSION_ERROR_KEY__TRANSLATION_INCOMPLETE,
        params: { languages: partiallyFilled.join(', ') },
      },
      baseEmail,
      translations: [],
      languagesMissingContent: [],
    };
  }

  const translations = candidates.filter(({ email }) => isComplete(email));

  const languagesMissingContent = contentIsSentForPurpose(purpose)
    ? [{ language, email: baseEmail }, ...translations]
        .filter(({ email }) => !filled(email.content))
        .map((entry) => entry.language.toUpperCase())
    : [];

  return {
    error: null,
    baseEmail,
    translations,
    languagesMissingContent,
  };
};
